import {Section, SectionHeader, SubSectionHeader} from '../../../components/Form/FormStyles';
import {EditedProject, visibleFundingParticipations} from '../EditedProject';
import {AcresRestoredControl} from '../MultipleProjectTypes/AcresRestoredControl';
import {AgreementNumberControl} from './AgreementNumberControl';
import {CommitmentFiscalYearControl} from './CommitmentFiscalYearControl';
import {CommitmentsControl} from './CommitmentsControl';
import {ForestControl} from './ForestControl';
import {FundingPartnersControl} from '../MultipleProjectTypes/FundingPartnersControl';
import {ImpactStoryCompletedControl} from '../MultipleProjectTypes/ImpactStoryCompletedControl';
import {ImpactStoryInProgressControl} from '../MultipleProjectTypes/ImpactStoryInProgressControl';
import {LocationControl} from '../MultipleProjectTypes/LocationControl';
import { MapControl } from '../MultipleProjectTypes/Map/MapControl';
import {NameControl} from '../MultipleProjectTypes/NameControl';
import {PlantingPartnerControl} from '../MultipleProjectTypes/PlantingPartnerControl';
import {PlantingTimeframeControl} from './PlantingTimeframeControl';
import {ProjectIdControl} from '../MultipleProjectTypes/ProjectIdControl';
import {ProjectStatusControl} from '../MultipleProjectTypes/ProjectStatusControl';
import {ProjectTypeControl} from '../MultipleProjectTypes/ProjectTypeControl';
import {DemoProjectStatusControl} from '../MultipleProjectTypes/DemoProjectStatusControl';
import {ShowInLIDControl} from '../MultipleProjectTypes/ShowInLIDControl';
import {ProjectYearsControl} from '../MultipleProjectTypes/ProjectYearsControl';
import {ReasonForPlantingControl} from '../MultipleProjectTypes/ReasonForPlantingControl';
import {ScalabilityControl} from '../MultipleProjectTypes/ScalabilityControl';
import {ImpactsControl} from '../MultipleProjectTypes/ImpactsControl';
import {ProposedProjectCostControl} from '../MultipleProjectTypes/ProposedProjectCostControl';
import {TotalTreeCostControl} from './TotalTreeCostControl';
import {TreeSpeciesControl} from '../MultipleProjectTypes/TreeSpeciesControl';
import {TTFDBHControl} from './TTFDBHControl';
import {TTFTreesPlantedControl} from './TTFTreesPlantedControl';
import {ImagesSection} from '../MultipleProjectTypes/ImagesSection';
import {ImpactStoryUneditedControl} from '../MultipleProjectTypes/ImpactStoryFromUnedited';
import {InfoAlert} from '../../../components/Alerts/Alerts';
import {ImpactStoryInProgressShortControl} from '../MultipleProjectTypes/ImpactStoryInProgressShortControl';
import {ImpactStoryCompletedShortControl} from '../MultipleProjectTypes/ImpactStoryCompletedShortControl';
import {NumberOfIndirectPeopleImpactedControl} from '../Community/NumberOfIndirectPeopleImpactedControl';
import {BenefitsSection} from '../MultipleProjectTypes/BenefitsSection';

export function ReforestationProject({
    project,
    setProjectAndValidate,
    completedProjectSetProjectAndValidate,
    newProject,
    navigationPromptActive,
    setNavigationPromptActive,
    registerSave,
    registerHasChanges,
    hasChangesUpdated,
    generateShortImpactStory,
    proposalMode,
    reloadProject
}: {
    project: EditedProject;
    setProjectAndValidate: (project: EditedProject) => void;
    completedProjectSetProjectAndValidate: (project: EditedProject) => void;
    newProject?: boolean;
    navigationPromptActive: boolean;
    setNavigationPromptActive: (navigationPromptActive: boolean) => void;
    registerSave: (f: (projectId: number) => void) => void;
    registerHasChanges: (f: () => boolean) => void;
    hasChangesUpdated: () => void;
    generateShortImpactStory: (originalText: string) => Promise<string>;
    proposalMode?: boolean;
    reloadProject: () => void;
}) {
    return (
        <>
            {!proposalMode && (
                <Section>
                    {project.ControlledByAutomation && (
                        <InfoAlert style={{width: '100%', maxWidth: '1100px'}}>
                            This project is being automatically updated from D365. Most fields are
                            read-only on this page. To update them, go to the{' '}
                            <a
                                href={`https://arbordayfarm.crm.dynamics.com/main.aspx?appid=aaf69a85-59da-4da2-9c1b-219a888ed0e8&pagetype=entityrecord&etn=stn_plantingproject&id=${project.D365PlantingProjectId}`}
                                target="_blank"
                                rel="noopener noreferrer">
                                D365 planting project
                            </a>
                            .
                        </InfoAlert>
                    )}
                    <ProjectStatusControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                        completedProjectSetProjectAndValidate={completedProjectSetProjectAndValidate}
                    />
                </Section>
            )}

            <Section>
                <SectionHeader>{proposalMode ? 'What is the proposal?' : 'What is the project?'}</SectionHeader>
                {!proposalMode && <ProjectIdControl project={project} />}
                {!proposalMode && (
                    <DemoProjectStatusControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
                {!proposalMode && (
                    <ShowInLIDControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
                {!proposalMode && (
                    <ProjectTypeControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
                <NameControl project={project} setProjectAndValidate={setProjectAndValidate} />
                {proposalMode && (
                    <ScalabilityControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
            </Section>

            <Section>
                <SectionHeader>{proposalMode ? 'Where is the proposal?' : 'Where is the project?'}</SectionHeader>
                <LocationControl project={project} setProjectAndValidate={setProjectAndValidate} />
                {!proposalMode && (
                    <ForestControl project={project} setProjectAndValidate={setProjectAndValidate} />
                )}
                <MapControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                    registerSave={registerSave}
                    registerHasChanges={registerHasChanges}
                    hasChangesUpdated={hasChangesUpdated}
                    navigationPromptActive={navigationPromptActive}
                    setNavigationPromptActive={setNavigationPromptActive}
                    proposalMode={proposalMode}
                />
            </Section>

            <Section>
                <SectionHeader>{proposalMode ? 'When is the proposal?' : 'When is the project?'}</SectionHeader>
                <ProjectYearsControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                    proposalMode={proposalMode}
                />
                {!proposalMode && (
                    <PlantingTimeframeControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
            </Section>

            <Section>
                <SectionHeader>{proposalMode ? 'Why is the proposal being done?' : 'Why is the project being done?'}</SectionHeader>
                <ReasonForPlantingControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
                {proposalMode && (
                    <ImpactsControl project={project} setProjectAndValidate={setProjectAndValidate} />
                )}
                {!proposalMode && (
                    <ImpactStoryUneditedControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                        disabled={project.ControlledByAutomation}
                    />
                )}
                <ImpactStoryInProgressControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                    disabled={project.ControlledByAutomation}
                    label={proposalMode ? 'Proposal Impact story' : undefined}
                />
                {!proposalMode && (
                    <ImpactStoryInProgressShortControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                        generateShortImpactStory={generateShortImpactStory}
                    />
                )}
                {!proposalMode && (
                    <ImpactStoryCompletedControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                        disabled={project.ControlledByAutomation}
                    />
                )}
                {!proposalMode && (
                    <ImpactStoryCompletedShortControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                        generateShortImpactStory={generateShortImpactStory}
                    />
                )}
            </Section>

            <Section>
                <SectionHeader>{proposalMode ? 'Who is participating in the proposal?' : 'Who is participating in the project?'}</SectionHeader>
                <PlantingPartnerControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
                {proposalMode && (
                    <ProposedProjectCostControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
                {!proposalMode && (
                    <CommitmentsControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
                {!proposalMode && (
                    <CommitmentFiscalYearControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
                {!proposalMode && (
                    <AgreementNumberControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
                {!proposalMode && (
                    <TotalTreeCostControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
                <FundingPartnersControl
                    participations={visibleFundingParticipations(project)}
                    projectId={project.Id}
                    projectType={project.ProjectType.current}
                    onParticipationChange={reloadProject}
                    proposalMode={proposalMode}
                />
                {!proposalMode && (
                    <>
                        <SubSectionHeader>People Impacted</SubSectionHeader>
                        <NumberOfIndirectPeopleImpactedControl
                            project={project}
                            setProjectAndValidate={setProjectAndValidate}
                        />
                    </>
                )}
            </Section>

            <Section>
                <SectionHeader>What is being planted?</SectionHeader>
                <TTFTreesPlantedControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
                {!proposalMode && (
                    <TTFDBHControl project={project} setProjectAndValidate={setProjectAndValidate} />
                )}
                <AcresRestoredControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
                {!proposalMode && (
                    <TreeSpeciesControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
            </Section>

            {!proposalMode && <BenefitsSection project={project} />}

            {!proposalMode && (
                <ImagesSection
                    newProject={newProject}
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
            )}
        </>
    );
}
